import React, { useEffect, useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';

// Add custom CSS animations
const customStyles = `
  @keyframes confetti-fall {
    0% { transform: translateY(-100vh) rotate(0deg); }
    100% { transform: translateY(100vh) rotate(360deg); }
  }
  
  @keyframes sparkle-twinkle {
    0%, 100% { opacity: 0.3; transform: scale(1); }
    50% { opacity: 1; transform: scale(1.2); }
  }
  
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
  }
  
  .confetti-animation {
    animation: confetti-fall 15s linear infinite;
  }
  
  .sparkle-animation {
    animation: sparkle-twinkle 8s ease-in-out infinite;
  }
  
  .float-animation-1 {
    animation: float 8s ease-in-out infinite;
  }
  
  .float-animation-2 {
    animation: float 10s ease-in-out infinite;
  }
  
  .float-animation-3 {
    animation: float 12s ease-in-out infinite;
  }
  
  .float-animation-4 {
    animation: float 14s ease-in-out infinite;
  }
`;

// Inject styles into document head
if (typeof document !== 'undefined' && !document.getElementById('celebration-styles')) {
  const styleSheet = document.createElement("style");
  styleSheet.id = 'celebration-styles';
  styleSheet.innerText = customStyles;
  document.head.appendChild(styleSheet);
}

const InstallationCelebration = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const milestones = [
    {
      year: "1991",
      title: "Foundation",
      description: "LEO Club of Colombo Circle was chartered",
      icon: "Star",
      color: "from-blue-500 to-purple-600"
    },
    {
      year: "2000s",
      title: "Growth Era",
      description: "Expanded community service projects",
      icon: "TrendingUp",
      color: "from-green-500 to-teal-600"
    },
    {
      year: "2010s",
      title: "Digital Age",
      description: "Embraced technology for greater impact",
      icon: "Zap",
      color: "from-orange-500 to-red-600"
    },
    {
      year: "2025",
      title: "35th Year",
      description: "Celebrating legacy and future vision",
      icon: "Crown",
      color: "from-purple-500 to-pink-600"
    }
  ];

  return (
    <section ref={sectionRef} className="relative py-20 bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        {/* Confetti */}
        {[...Array(20)].map((_, i) => (
          <div
            key={`confetti-${i}`}
            className={`absolute w-3 h-3 ${i % 4 === 0 ? 'bg-yellow-400' :
              i % 4 === 1 ? 'bg-pink-400' :
                i % 4 === 2 ? 'bg-blue-400' : 'bg-green-400'
              } rounded-full confetti-animation`}
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${i * 0.8}s`,
              animationDuration: `${12 + Math.random() * 8}s`
            }}
          />
        ))}

        {/* Sparkles */}
        {[...Array(15)].map((_, i) => (
          <div
            key={`sparkle-${i}`}
            className="absolute text-yellow-300 text-2xl sparkle-animation"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${i * 1.2}s`
            }}
          >
            ✨
          </div>
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className={`mb-8 transition-all duration-1500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h2 className="text-5xl lg:text-7xl font-bold text-white mb-4">
              🎉 Stepping into
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500">
                {" "}35th Year
              </span>
            </h2>
            <p className="text-xl lg:text-2xl text-purple-200">
              Installation Ceremony 2025/26
            </p>
          </div>

          <div className={`relative inline-block transition-all duration-1800 delay-500 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}`}>
            <div className="text-8xl lg:text-9xl font-black text-transparent bg-clip-text bg-gradient-to-r from-pink-400 via-purple-400 to-indigo-400 animate-pulse">
              35
            </div>
            <div className="absolute -top-4 -right-4 text-4xl animate-bounce">🎊</div>
            <div className="absolute -bottom-4 -left-4 text-4xl animate-bounce" style={{ animationDelay: '0.5s' }}>🎈</div>
          </div>

          <p className="text-lg text-purple-100 max-w-3xl mx-auto mt-6">
            From humble beginnings in 1991 to becoming a beacon of youth leadership in Colombo,
            we celebrate 34 incredible years of service and step boldly into our 35th year of making a difference!
          </p>
        </div>

        {/* Journey Timeline */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {milestones.map((milestone, index) => (
            <div
              key={index}
              className={`relative group transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}
              style={{ transitionDelay: `${800 + index * 300}ms` }}
            >
              <div className={`bg-gradient-to-br ${milestone.color} p-6 rounded-2xl shadow-2xl transform transition-all duration-500 hover:scale-105 hover:shadow-3xl float-animation-${index + 1}`}
                style={{ animationDelay: `${index + Math.random() * 4}s` }}>
                <div className="text-center text-white">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 backdrop-blur-sm">
                    <Icon name={milestone.icon} size={32} className="text-white" />
                  </div>
                  <div className="text-2xl font-bold mb-2">{milestone.year}</div>
                  <h3 className="text-lg font-semibold mb-2">{milestone.title}</h3>
                  <p className="text-sm opacity-90">{milestone.description}</p>
                </div>

                {/* Floating particles */}
                <div className="absolute -top-2 -right-2 w-4 h-4 bg-yellow-300 rounded-full animate-ping"></div>
                <div className="absolute -bottom-2 -left-2 w-3 h-3 bg-pink-300 rounded-full animate-ping" style={{ animationDelay: '1s' }}></div>
              </div>
            </div>
          ))}
        </div>

        {/* Milestone Reflection */}
        <div className="text-center">
          <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 border border-white/20 max-w-4xl mx-auto">
            <h3 className="text-3xl font-bold text-white mb-4">
              35 Years of Impact ✨
            </h3>
            <p className="text-purple-100 text-lg mb-6">
              Three and a half decades of youth empowerment, community service, and leadership development.
              As we step into our 35th year, we honor our past achievements while embracing new opportunities to serve and lead.
            </p>

            <div className="mt-8 text-purple-200">
              <p className="text-lg font-semibold">
                Celebrating our heritage • Embracing our future • Continuing our service
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InstallationCelebration;